/* eslint-disable indent */
import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Modal, notification, Select, DatePicker, Input, Tooltip } from 'antd'
import { Icon } from '@iconify/react'
import { LoadingOutlined } from '@ant-design/icons'
import { adminServices } from '../../../../services'
import moment from 'moment'
import { DIRECT_DEBIT_PROVIDER } from '../../../../utils/constants'

const bodyStyle = {
  padding: 0
}

// Function to disable dates before tomorrow
const disablePastDates = current => {
  return current && current < moment().endOf('day') // Disable dates before tomorrow
}

// Format date to YYYY-MM-DD format for API
const formatDateForAPI = date => {
  if (!date) return undefined
  return moment(date).format('YYYY-MM-DD')
}

const DirectDebitFormModal = ({
  visible,
  onCancel,
  loanId,
  action,
  bvn,
  userId,
  provider,
  mandateType: propMandateType
}) => {
  const [loading, setLoading] = useState(false)

  // New state variables for the form
  // Set default to 'reoccurring' if provider is 'mono', otherwise empty
  const [debitOption, setDebitOption] = useState(provider === 'mono' ? 'reoccurring' : '')
  const [mandateType, setMandateType] = useState(propMandateType || '') // No default for mandate type
  const [debitType, setDebitType] = useState('fixed') // Set fixed as default for debit type
  const [frequency, setFrequency] = useState('')
  const [startDate, setStartDate] = useState(null)
  const [chargeDate, setChargeDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [amount, setAmount] = useState('')
  const [gracePeriod, setGracePeriod] = useState('')
  const [minimumDue, setMinimumDue] = useState('')

  // Memoize the handleSubmission function
  const handleSubmission = useCallback(async () => {
    if (!bvn || !debitOption || !amount) {
      notification.error({
        message: 'Validation Error',
        description: 'Amount and debit option are required'
      })
      return
    }

    // Only require mandate type and debit type for Mono provider
    if (provider === 'mono' && (!mandateType || !debitType)) {
      notification.error({
        message: 'Validation Error',
        description: 'Mandate type and debit type are required for Mono provider'
      })
      return
    }

    if (Number(amount) < 1000) {
      notification.error({
        message: 'Validation Error',
        description: 'Amount cannot be less than ₦1000'
      })
      return
    }

    if (debitOption === 'reoccurring') {
      if (!startDate || !endDate) {
        notification.error({
          message: 'Validation Error',
          description: 'Start date and End date are required for recurring payments'
        })
        return
      }

      // Only require frequency if mandate type is not 'variable' for mono provider
      if (provider !== 'mono' || (provider === 'mono' && debitType !== 'variable')) {
        if (!frequency) {
          notification.error({
            message: 'Validation Error',
            description: 'Frequency is required for recurring payments'
          })
          return
        }
      }
    }

    if (debitOption === 'one-time') {
      if (!chargeDate) {
        notification.error({
          message: 'Validation Error',
          description: 'Charge date is required for one-time payments'
        })
        return
      }
    }

    // Validate grace period and minimum due for fixed debit type
    if (provider === 'mono' && debitType === 'fixed' && debitOption === 'reoccurring') {
      // Only validate if values are provided (fields are optional)
      if (gracePeriod !== '' && Number(gracePeriod) < 1) {
        notification.error({
          message: 'Validation Error',
          description: 'Grace period must be at least 1 day when provided'
        })
        return
      }

      if (minimumDue !== '' && Number(minimumDue) < 100) {
        notification.error({
          message: 'Validation Error',
          description: 'Minimum due must be at least ₦100 when provided'
        })
        return
      }
    }

    setLoading(true)

    try {
      const payload = {
        user_id: userId,
        bvn: bvn,
        redirectUrl: `${window.location.origin}/direct-debit-consent-success`,
        amount,
        is_recurring: debitOption === 'reoccurring',
        service_provider: provider === 'flick' ? DIRECT_DEBIT_PROVIDER.FLICK : provider,
        loan_id: loanId ? loanId : undefined
      }

      // Only include mono-specific fields when provider is mono and values exist
      if (provider === 'mono') {
        if (mandateType) payload.mandate_type = mandateType
        if (debitType) payload.debit_type = debitType
      }

      // Add grace period and minimum due for fixed debit type (only if provided)
      if (provider === 'mono' && debitType === 'fixed' && debitOption === 'reoccurring') {
        if (gracePeriod !== '') payload.grace_period = Number(gracePeriod)
        if (minimumDue !== '') payload.minimum_due = Number(minimumDue)
      }

      // For Flick provider, hardcode mandate_type and debit_type
      if (provider === 'flick' || provider === DIRECT_DEBIT_PROVIDER.FLICK) {
        payload.mandate_type = 'emandate'
        payload.debit_type = 'fixed'
      }

      const oneTimePayload = {
        ...payload,
        charge_date: formatDateForAPI(chargeDate)
      }

      const recurringPayload = {
        ...payload,
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate),
        frequency: provider === 'mono' && debitType === 'variable' ? undefined : frequency
      }

      const response = await adminServices.directDebitInitialize(
        debitOption === 'reoccurring' ? recurringPayload : oneTimePayload
      )

      const link = response?.data?.data?.url || response?.data?.link
      notification.success({
        message: 'Success',
        description: 'Consent link generated successfully'
      })
      action(link)
    } catch (error) {
      notification.error({
        message: 'Something went wrong',
        description: error?.response?.data?.message || error?.message
      })
    } finally {
      setLoading(false)
    }
  }, [
    action,
    debitOption,
    frequency,
    startDate,
    endDate,
    chargeDate,
    amount,
    provider,
    loanId,
    mandateType,
    gracePeriod,
    minimumDue
  ])

  // Clear form when modal is closed, set defaults when modal opens
  useEffect(() => {
    if (!visible) {
      setDebitOption('')
      setMandateType('') // Reset to empty
      setDebitType('fixed') // Reset to default
      setFrequency('')
      setStartDate(null)
      setEndDate(null)
      setChargeDate(null)
      setAmount('')
      setGracePeriod('')
      setMinimumDue('')
    } else {
      // When modal opens, set default debit option to 'reoccurring' for mono provider
      if (provider === 'mono') {
        setDebitOption('reoccurring')
      }
    }
  }, [visible, provider])

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      className='info-modal'
      closeIcon={
        <Icon
          icon='iconamoon:close-thin'
          color='#b1b1ff'
          className='mt-4 mr-8 text-white text-4xl rounded-full bg-white'
        />
      }
      bodyStyle={bodyStyle}
      width={800}
    >
      <div className='modal-header bg-primary px-6 flex justify-between pb-4'>
        <h2 className='font-bold tracking-tight mt-4 text-white text-2xl'>Initiate Direct Debit</h2>
      </div>
      <div className='px-4 pb-8 mt-8'>
        <p className='font-bold text-primary'>
          Fill the information below to initiate direct debit for customer
        </p>
        <div className='flex flex-col gap-4 justify-center w-full'>
          <div className='grid grid-cols-2 gap-8'>
            <div>
              <p className='py-2 font-bold'>Select direct debit option</p>
              <Select
                value={debitOption}
                onChange={setDebitOption}
                placeholder='Select direct debit option'
                className='w-full'
                options={[
                  { label: 'Re-occurring debit', value: 'reoccurring' },
                  // Exclude one-time option for mono provider
                  ...(provider !== 'mono' ? [{ label: 'One-time debit', value: 'one-time' }] : [])
                ]}
              />
            </div>
            {provider === 'mono' && debitOption === 'reoccurring' ? (
              <div>
                <p className='py-2 font-bold'>Debit type</p>
                <Select
                  value={debitType}
                  onChange={setDebitType}
                  placeholder='Select debit type'
                  className='w-full'
                  options={[
                    { label: 'Fixed', value: 'fixed' },
                    { label: 'Variable', value: 'variable' }
                  ]}
                />
              </div>
            ) : debitOption === 'reoccurring' ? (
              <div>
                <p className='py-2 font-bold'>Frequency</p>
                <Select
                  value={frequency}
                  onChange={setFrequency}
                  placeholder='Select frequency'
                  className='w-full'
                  options={[
                    { label: 'Daily', value: 'daily' },
                    { label: 'Weekly', value: 'weekly' },
                    { label: 'Monthly', value: 'monthly' },
                    { label: 'Yearly', value: 'yearly' }
                  ]}
                />
              </div>
            ) : (
              <div />
            )}
          </div>
          {provider === 'mono' && (
            <div className='grid grid-cols-2 gap-8'>
              <div>
                <p className='py-2 font-bold'>Select mandate type</p>
                <Select
                  value={mandateType}
                  onChange={setMandateType}
                  placeholder='Select mandate type'
                  className='w-full'
                  options={[
                    { label: 'Emandate', value: 'emandate' },
                    { label: 'GSM', value: 'gsm' }
                  ]}
                />
              </div>
              {debitOption === 'reoccurring' &&
              (provider !== 'mono' || (provider === 'mono' && debitType !== 'variable')) ? (
                <div>
                  <p className='py-2 font-bold'>Frequency</p>
                  <Select
                    value={frequency}
                    onChange={setFrequency}
                    placeholder='Select frequency'
                    className='w-full'
                    options={[
                      { label: 'Daily', value: 'daily' },
                      { label: 'Weekly', value: 'weekly' },
                      { label: 'Monthly', value: 'monthly' },
                      { label: 'Yearly', value: 'yearly' }
                    ]}
                  />
                </div>
              ) : (
                <div />
              )}
            </div>
          )}
          {debitOption === 'reoccurring' ? (
            <div className='grid grid-cols-2 gap-8'>
              <div>
                <p className='py-2 font-bold'>Select Start Date</p>
                <DatePicker
                  value={startDate}
                  onChange={date => setStartDate(date)}
                  style={{ width: '100%' }}
                  disabledDate={disablePastDates}
                  format='YYYY-MM-DD'
                />
              </div>
              <div>
                <p className='py-2 font-bold'>Select End Date</p>
                <DatePicker
                  value={endDate}
                  onChange={date => setEndDate(date)}
                  style={{ width: '100%' }}
                  disabledDate={disablePastDates}
                  format='YYYY-MM-DD'
                />
              </div>
            </div>
          ) : (
            <div>
              <p className='py-2 font-bold'>Charge Date</p>
              <DatePicker
                value={chargeDate}
                onChange={date => setChargeDate(date)}
                style={{ width: '100%' }}
                disabledDate={disablePastDates}
                format='YYYY-MM-DD'
              />
              <small className='text-gray-500'>Format: YYYY-MM-DD (e.g., 2023-12-31)</small>
            </div>
          )}

          <div>
            <p className='py-2 font-bold'>Amount</p>
            <Input
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder='Enter amount'
              prefix='₦'
            />
          </div>

          {/* Grace Period and Minimum Due fields for fixed debit type */}
          {provider === 'mono' && debitType === 'fixed' && debitOption === 'reoccurring' && (
            <div className='grid grid-cols-2 gap-8'>
              <div>
                <div className='flex items-center gap-2'>
                  <p className='py-2 font-bold'>Grace period (days)</p>
                  <Tooltip title='When an account is due for debit, the customer may not have sufficient balance in their account for the debit to go through successfully. Grace period takes in the number of days that you want the system to continue to attempt to debit the user, until the debit goes through successfully.'>
                    <Icon icon='mdi:information' className='text-blue-500 cursor-help' />
                  </Tooltip>
                </div>
                <Input
                  type='number'
                  value={gracePeriod}
                  onChange={e => setGracePeriod(e.target.value)}
                  placeholder='Enter grace period in days'
                  min='1'
                />
              </div>
              <div>
                <div className='flex items-center gap-2'>
                  <p className='py-2 font-bold'>Minimum due (₦)</p>
                  <Tooltip title='This field expects the amount in a case of failed debits due to insufficient funds, then the minimum_due is attempted.'>
                    <Icon icon='mdi:information' className='text-blue-500 cursor-help' />
                  </Tooltip>
                </div>
                <Input
                  type='number'
                  value={minimumDue}
                  onChange={e => setMinimumDue(e.target.value)}
                  placeholder='Enter minimum due amount'
                  prefix='₦'
                  min='100'
                />
              </div>
            </div>
          )}

          <div className='flex justify-center'>
            <button
              onClick={handleSubmission}
              className='bg-primary rounded-md md:px-12 py-2 text-white outline border border-gray-300 flex items-center justify-center'
            >
              {loading ? (
                <div className='flex items-center gap-3'>
                  <LoadingOutlined spin /> Processing...
                </div>
              ) : (
                'Generate consent link'
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

DirectDebitFormModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  bvn: PropTypes.string,
  userId: PropTypes.string,
  loanId: PropTypes.string,
  provider: PropTypes.string,
  mandateType: PropTypes.string,
  action: PropTypes.func.isRequired
}

// Wrap the entire component with React.memo
export default React.memo(DirectDebitFormModal)
